/*
 * Stale-chunk recovery.
 *
 * Every deploy renames the hashed JS chunks and drops the previous set; a tab
 * that loaded index.html before the deploy will fail its next lazy page
 * import (the worker even answers the old chunk URL with the SPA's HTML, so
 * the module parse fails). The remedy is simply reloading the page, which
 * fetches the fresh index.html and chunk manifest.
 *
 * A sessionStorage timestamp guards against reload loops: at most one
 * automatic reload per 30s window — anything still failing after that is a
 * real error and should surface.
 */

const RELOAD_MARKER_KEY = 'pooram-stale-chunk-reload-at';
const RELOAD_LOOP_WINDOW_MS = 30_000;

export function isStaleChunkError(error: unknown): boolean {
  const message =
    error instanceof Error ? `${error.name} ${error.message}` :
    typeof error === 'string' ? error : '';
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|chunkloaderror|unable to preload css|expected a javascript.*module/i.test(message);
}

/** Reload to pick up the new build. Returns false if a reload was already
 *  attempted moments ago (likely a genuine failure — let it surface). */
export function attemptStaleChunkReload(): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_MARKER_KEY) ?? 0);
  } catch {
    /* sessionStorage unavailable — still reload once per page lifecycle */
  }
  if (Date.now() - last < RELOAD_LOOP_WINDOW_MS) return false;
  try {
    sessionStorage.setItem(RELOAD_MARKER_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  window.location.reload();
  return true;
}

export function clearStaleChunkMarker(): void {
  try {
    sessionStorage.removeItem(RELOAD_MARKER_KEY);
  } catch {
    /* ignore */
  }
}
